import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Plus, Pencil, Trash2, GripVertical, Check, X } from 'lucide-react';
interface CategoryManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: string[];
  onUpdateCategories: (categories: string[]) => void;
}
export function CategoryManagerModal({
  isOpen,
  onClose,
  categories,
  onUpdateCategories
}: CategoryManagerModalProps) {
  const [editableCategories, setEditableCategories] = useState<string[]>(categories.filter(c => c !== 'All'));
  const [newCategory, setNewCategory] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const handleAddCategory = () => {
    if (newCategory.trim() && !editableCategories.includes(newCategory.trim())) {
      setEditableCategories([...editableCategories, newCategory.trim()]);
      setNewCategory('');
    }
  };
  const handleStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditValue(editableCategories[index]);
  };
  const handleSaveEdit = () => {
    if (editingIndex !== null && editValue.trim()) {
      const updated = [...editableCategories];
      updated[editingIndex] = editValue.trim();
      setEditableCategories(updated);
      setEditingIndex(null);
      setEditValue('');
    }
  };
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };
  const handleDelete = (index: number) => {
    setEditableCategories(editableCategories.filter((_, i) => i !== index));
    setDeleteConfirm(null);
  };
  const handleSave = () => {
    onUpdateCategories(['All', ...editableCategories]);
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Manage Categories" data-id="element-369">
      <div className="space-y-4" data-id="element-370">
        <div className="flex gap-2" data-id="element-371">
          <input type="text" value={newCategory} onChange={e => setNewCategory(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddCategory()} placeholder="New category name..." className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm" data-id="element-372" />
          <Button variant="primary" onClick={handleAddCategory} disabled={!newCategory.trim()} data-id="element-373">
            <Plus className="w-4 h-4" data-id="element-374" />
          </Button>
        </div>
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-64 overflow-y-auto" data-id="element-375">
          {editableCategories.length === 0 ? <div className="p-4 text-center text-gray-500 text-sm" data-id="element-376">
              No categories yet. Add one above.
            </div> : editableCategories.map((category, index) => <div key={index} className="flex items-center gap-3 p-3 hover:bg-gray-50 group" data-id="element-377">
                <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" data-id="element-378" />
                {editingIndex === index ? <div className="flex-1 flex items-center gap-2" data-id="element-379">
                    <input type="text" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') handleSaveEdit();
              if (e.key === 'Escape') handleCancelEdit();
            }} className="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" autoFocus data-id="element-380" />
                    <button onClick={handleSaveEdit} className="p-1 text-green-600 hover:bg-green-50 rounded" data-id="element-381">
                      <Check className="w-4 h-4" data-id="element-382" />
                    </button>
                    <button onClick={handleCancelEdit} className="p-1 text-gray-400 hover:bg-gray-100 rounded" data-id="element-383">
                      <X className="w-4 h-4" data-id="element-384" />
                    </button>
                  </div> : deleteConfirm === index ? <div className="flex-1 flex items-center justify-between" data-id="element-385">
                    <span className="text-sm text-red-600" data-id="element-386">
                      Delete "{category}"?
                    </span>
                    <div className="flex gap-2" data-id="element-387">
                      <button onClick={() => handleDelete(index)} className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700" data-id="element-388">
                        Delete
                      </button>
                      <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300" data-id="element-389">
                        Cancel
                      </button>
                    </div>
                  </div> : <>
                    <span className="flex-1 text-sm text-gray-700" data-id="element-390">
                      {category}
                    </span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-391">
                      <button onClick={() => handleStartEdit(index)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" data-id="element-392">
                        <Pencil className="w-3.5 h-3.5" data-id="element-393" />
                      </button>
                      <button onClick={() => setDeleteConfirm(index)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" data-id="element-394">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-395" />
                      </button>
                    </div>
                  </>}
              </div>)}
        </div>
        <p className="text-xs text-gray-500" data-id="element-396">
          Note: "All" category is automatically included and cannot be removed.
        </p>
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-397">
          <Button variant="secondary" onClick={onClose} data-id="element-398">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} data-id="element-399">
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>;
}